import React, { useState, useEffect, useRef } from "react";
import { Button } from "primereact/button";
import { InputNumber } from "primereact/inputnumber";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { useConfirmDialog } from "../../utils/ConfirmDialog/ConfirmDialogProvider";

const DynamicPayment = ({ visible, onHide, data, onGenerate }) => {
    const { orderAmount, term, rate, firstPaymentDate } = data; // firstPaymentDate eklendi
    const [payments, setPayments] = useState([]);
    const [equalMonthlyPayment, setEqualMonthlyPayment] = useState(0);
    const [totalPayment, setTotalPayment] = useState(0);

    const toast = useRef(null);
    const { showDialog } = useConfirmDialog();

    // Tarih hesaplayıcı fonksiyon
    const getPaymentDate = (monthIndex) => {
        if (!firstPaymentDate) return null;

        const baseDate = new Date(firstPaymentDate);
        baseDate.setMonth(baseDate.getMonth() + monthIndex);
        return baseDate;
    };

    // Tarih formatter fonksiyon
    const formatPaymentDate = (date) => {
        if (!date) return null;
        return date.toLocaleDateString('tr-TR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    useEffect(() => {
        if (visible) {
            const calculatedPayments = calculatePaymentPlan();
            setPayments(calculatedPayments);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, orderAmount, term, rate, firstPaymentDate]);

    const calculatePaymentPlan = () => {
        const principal = parseFloat(orderAmount) || 0;
        const months = parseInt(term) || 0;
        const monthlyRate = parseFloat(rate) / 100;

        if (principal <= 0 || months <= 0 || monthlyRate < 0) {
            return [];
        }

        let monthlyPayment, totalPaymentAmount;

        if (monthlyRate === 0) {
            monthlyPayment = principal / months;
            totalPaymentAmount = principal;
        } else {
            monthlyPayment = (principal * monthlyRate) / (1 - Math.pow(1 + monthlyRate, -months));
            totalPaymentAmount = monthlyPayment * months;
        }

        setEqualMonthlyPayment(monthlyPayment);
        setTotalPayment(totalPaymentAmount);

        let remainingPrincipal = principal;
        const paymentPlan = [];

        for (let i = 0; i < months; i++) {
            let interest = monthlyRate === 0 ? 0 : remainingPrincipal * monthlyRate;
            let principalPayment, payment;

            if (i === months - 1) {
                principalPayment = remainingPrincipal;
                payment = principalPayment + interest;
            } else {
                if (monthlyRate === 0) {
                    principalPayment = monthlyPayment;
                    payment = principalPayment;
                } else {
                    payment = monthlyPayment;
                    principalPayment = payment - interest;

                    if (principalPayment > remainingPrincipal) {
                        principalPayment = remainingPrincipal;
                        payment = principalPayment + interest;
                    }
                }
            }

            remainingPrincipal = Math.max(0, remainingPrincipal - principalPayment);

            payment = parseFloat(payment.toFixed(2));
            principalPayment = parseFloat(principalPayment.toFixed(2));
            interest = parseFloat(interest.toFixed(2));
            remainingPrincipal = parseFloat(remainingPrincipal.toFixed(2));

            const paymentDate = getPaymentDate(i);

            paymentPlan.push({
                month: i + 1,
                paymentDate: paymentDate,
                amount: payment,
                principal: principalPayment,
                interest: interest,
                remainingPrincipal: remainingPrincipal,
                isModified: false
            });

            if (remainingPrincipal === 0 && i < months - 1) {
                for (let j = i + 1; j < months; j++) {
                    const nextPaymentDate = getPaymentDate(j);
                    paymentPlan.push({
                        month: j + 1,
                        paymentDate: nextPaymentDate,
                        amount: 0,
                        principal: 0,
                        interest: 0,
                        remainingPrincipal: 0,
                        isModified: false
                    });
                }
                break;
            }
        }

        return paymentPlan;
    };

    const recalculatePaymentPlan = () => {
        const principal = parseFloat(orderAmount);
        const monthlyRate = parseFloat(rate) / 100;
        const updatedPayments = [...payments];
        let remainingPrincipal = principal;

        for (let i = 0; i < updatedPayments.length; i++) {
            const payment = updatedPayments[i];
            let interest = remainingPrincipal * monthlyRate;
            let principalPayment;

            if (payment.isModified) {
                if (remainingPrincipal < payment.amount) {
                    interest = 0;
                    principalPayment = remainingPrincipal;
                } else {
                    principalPayment = Math.max(0, payment.amount - interest);
                }
            } else {
                if (remainingPrincipal < payment.amount) {
                    interest = 0;
                    principalPayment = remainingPrincipal;
                } else {
                    principalPayment = payment.amount - interest;
                }
            }

            if (remainingPrincipal - principalPayment < 0.01) {
                principalPayment = remainingPrincipal;
                interest = 0;
            }

            remainingPrincipal = Math.max(0, remainingPrincipal - principalPayment);

            updatedPayments[i] = {
                ...payment,
                principal: parseFloat(principalPayment.toFixed(2)),
                interest: parseFloat(interest.toFixed(2)),
                remainingPrincipal: parseFloat(remainingPrincipal.toFixed(2))
            };

            if (remainingPrincipal === 0 && i < updatedPayments.length - 1) {
                for (let j = i + 1; j < updatedPayments.length; j++) {
                    updatedPayments[j] = {
                        ...updatedPayments[j],
                        amount: 0,
                        principal: 0,
                        interest: 0,
                        remainingPrincipal: 0
                    };
                }
                break;
            }
        }

        setPayments(updatedPayments);
    };

    const handleAmountChange = (index, value) => {
        if (value === null) return;

        const updatedPayments = [...payments];
        const originalAmount = equalMonthlyPayment;
        const isValueDifferent = Math.abs(parseFloat(value) - originalAmount) > 0.01;

        updatedPayments[index] = {
            ...updatedPayments[index],
            amount: parseFloat(value.toFixed(2)),
            isModified: isValueDifferent
        };

        setPayments(updatedPayments);
    };

    const redistributePayments = () => {
        try {
            const modifiedPayments = payments.filter(p => p.isModified);
            const unmodifiedIndices = payments.map((p, i) => !p.isModified ? i : -1).filter(i => i !== -1);

            if (unmodifiedIndices.length === 0) {
                if (toast.current) {
                    toast.current.show({
                        severity: "warn",
                        summary: "Uyarı",
                        detail: "Tüm ödemeler değiştirilmiş. Dağıtılacak ödeme yok.",
                        life: 3000,
                    });
                }
                return;
            }

            const modifiedTotal = modifiedPayments.reduce((sum, p) => sum + p.amount, 0);
            const remainingAmount = totalPayment - modifiedTotal;

            if (remainingAmount <= 0) {
                if (toast.current) {
                    toast.current.show({
                        severity: "warn",
                        summary: "Uyarı",
                        detail: "Değiştirilmiş ödemeler toplam tutarı aşıyor.",
                        life: 3000,
                    });
                }
                return;
            }

            const equalAmount = parseFloat((remainingAmount / unmodifiedIndices.length).toFixed(2));
            const newPayments = [...payments];

            unmodifiedIndices.forEach(idx => {
                newPayments[idx] = {
                    ...newPayments[idx],
                    amount: equalAmount,
                    isModified: false
                };
            });

            if (unmodifiedIndices.length > 0) {
                const lastUnmodifiedIdx = unmodifiedIndices[unmodifiedIndices.length - 1];
                const totalUnmodified = equalAmount * unmodifiedIndices.length;
                const roundingDiff = remainingAmount - totalUnmodified;

                if (Math.abs(roundingDiff) > 0.01) {
                    newPayments[lastUnmodifiedIdx].amount = parseFloat((newPayments[lastUnmodifiedIdx].amount + roundingDiff).toFixed(2));
                }
            }

            setPayments(newPayments);

            setTimeout(() => {
                const principal = parseFloat(orderAmount);
                const monthlyRate = parseFloat(rate) / 100;
                let remainingPrincipal = principal;

                const updatedPayments = newPayments.map(payment => {
                    const interest = remainingPrincipal * monthlyRate;
                    const principalPayment = Math.max(0, payment.amount - interest);
                    remainingPrincipal = Math.max(0, remainingPrincipal - principalPayment);

                    return {
                        ...payment,
                        principal: parseFloat(principalPayment.toFixed(2)),
                        interest: parseFloat(interest.toFixed(2)),
                        remainingPrincipal: parseFloat(remainingPrincipal.toFixed(2))
                    };
                });

                setPayments(updatedPayments);
            }, 0);
        } catch (error) {
            console.error("Eşit dağıtma sırasında hata oluştu:", error);
            if (toast.current) {
                toast.current.show({
                    severity: "error",
                    summary: "Hata",
                    detail: "Eşit dağıtma işlemi sırasında bir hata oluştu.",
                    life: 3000,
                });
            }
        }
    };

    const resetPayments = () => {
        const calculatedPayments = calculatePaymentPlan();
        setPayments(calculatedPayments);
    };

    const handleGenerate = () => {
        const principal = parseFloat(orderAmount);
        const monthlyRate = parseFloat(rate) / 100;
        let remainingPrincipal = principal;

        const finalPayments = payments.map(payment => {
            const interest = remainingPrincipal * monthlyRate;
            const principalPayment = Math.max(0, payment.amount - interest);
            remainingPrincipal = Math.max(0, remainingPrincipal - principalPayment);

            return {
                month: payment.month,
                amount: payment.amount,
                principal: parseFloat(principalPayment.toFixed(2)),
                interest: parseFloat(interest.toFixed(2)),
                remainingPrincipal: parseFloat(remainingPrincipal.toFixed(2))
            };
        });

        const totalEntered = finalPayments.reduce((sum, p) => sum + p.amount, 0);
        if (Math.abs(totalEntered - totalPayment) > 1) {
            if (toast.current) {
                toast.current.show({
                    severity: "error",
                    summary: "Hata",
                    detail: `Girilen toplam tutar (${totalEntered.toFixed(2)}), toplam geri ödeme tutarıyla (${totalPayment.toFixed(2)}) eşleşmiyor!`,
                    life: 3000,
                });
            }
            return;
        }

        onGenerate(finalPayments);
        onHide();
    };

    const dialogFooter = (
        <div className="flex justify-end pt-4 dark:bg-gray-800">
            <Button
                label="Ödeme Planını Oluştur"
                onClick={() => showDialog({
                    message: "Ödeme planını kaydetmek istediğinize emin misiniz ?",
                    onConfirm: () => {
                        handleGenerate()
                    }
                })}
                className="bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-800 text-white px-6 py-2 rounded-md transition-colors"
            />
        </div>
    );

    return (
        <Dialog
            header="Dinamik Ödeme Planı"
            visible={visible}
            onHide={onHide}
            style={{ width: '90vw' }}
            footer={dialogFooter}
            maximizable
            className="dark:bg-gray-900 dark:text-gray-100"
            contentClassName="dark:bg-gray-900"
            headerClassName="dark:bg-gray-800 dark:text-gray-100"
        >
            <Toast ref={toast} />
            <div className="p-6 space-y-6 dark:bg-gray-900">
                <div className="space-y-4">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 space-y-3">
                        <p className="text-gray-700 dark:text-gray-300">
                            Standart Aylık Taksit:
                            <span className="font-semibold ml-2 dark:text-gray-100">
                                {equalMonthlyPayment.toFixed(2)} TL
                            </span>
                        </p>
                        <p className="text-gray-700 dark:text-gray-300">
                            Toplam Geri Ödeme:
                            <span className="font-semibold ml-2 dark:text-gray-100">
                                {totalPayment.toFixed(2)} TL
                            </span>
                        </p>
                        <p className="text-gray-700 dark:text-gray-300">
                            Ana Para:
                            <span className="font-semibold ml-2 dark:text-gray-100">
                                {orderAmount} TL
                            </span>
                        </p>
                        {firstPaymentDate && (
                            <p className="text-gray-700 dark:text-gray-300">
                                İlk Taksit Tarihi:
                                <span className="font-semibold ml-2 dark:text-gray-100">
                                    {formatPaymentDate(firstPaymentDate)}
                                </span>
                            </p>
                        )}
                        <div className="flex gap-3">
                            <Button
                                label="Kalan Tutarı Eşit Dağıt"
                                onClick={redistributePayments}
                                className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800 text-white px-4 py-2 rounded-md transition-colors"
                            />
                            <Button
                                label="Sıfırla"
                                onClick={resetPayments}
                                className="bg-gray-600 hover:bg-gray-700 dark:bg-gray-700 dark:hover:bg-gray-800 text-white px-4 py-2 rounded-md transition-colors"
                            />
                            <Button
                                label="Yeniden Hesapla"
                                onClick={recalculatePaymentPlan}
                                className="bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-800 text-white px-4 py-2 rounded-md transition-colors"
                            />
                        </div>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="min-w-full bg-white dark:bg-gray-800 rounded-lg shadow-sm">
                        <thead>
                            <tr className="bg-gray-50 dark:bg-gray-700">
                                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600 dark:text-gray-200">
                                    {firstPaymentDate ? 'Vade Tarihi' : 'Ay'}
                                </th>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600 dark:text-gray-200">Taksit Tutarı (₺)</th>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600 dark:text-gray-200">Anapara (₺)</th>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600 dark:text-gray-200">Faiz (₺)</th>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600 dark:text-gray-200">Kalan Anapara (₺)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {payments.map((payment, index) => (
                                <tr key={index} className={`border-b dark:border-gray-700 ${payment.isModified
                                    ? 'bg-blue-50 dark:bg-blue-900/30'
                                    : 'dark:bg-gray-800'}`}>
                                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                                        {firstPaymentDate && payment.paymentDate
                                            ? formatPaymentDate(payment.paymentDate)
                                            : payment.month
                                        }
                                    </td>
                                    <td className="px-4 py-3">
                                        <InputNumber
                                            value={payment.amount}
                                            onValueChange={(e) => handleAmountChange(index, e.value)}
                                            mode="decimal"
                                            minFractionDigits={2}
                                            maxFractionDigits={2}
                                            className={`w-32 ${payment.isModified
                                                ? 'bg-blue-50 border-blue-400 dark:bg-blue-900/30 dark:border-blue-500'
                                                : 'bg-white border-gray-200 dark:bg-gray-700 dark:border-gray-600'
                                                }`}
                                            inputClassName="w-full p-1 rounded-md text-sm dark:text-gray-200 dark:bg-gray-700"
                                        />
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                                        {payment.principal.toFixed(2)}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                                        {payment.interest.toFixed(2)}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                                        {payment.remainingPrincipal.toFixed(2)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr className="bg-gray-50 dark:bg-gray-700 font-semibold">
                                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-200">Toplam</td>
                                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-200">
                                    {payments.reduce((sum, p) => sum + p.amount, 0).toFixed(2)}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-200">
                                    {payments.reduce((sum, p) => sum + p.principal, 0).toFixed(2)}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-200">
                                    {payments.reduce((sum, p) => sum + p.interest, 0).toFixed(2)}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-200">-</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>
        </Dialog>
    );
};

export default DynamicPayment;